import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { LogTime } from '../../../../actions/forFrontEnd_Log';
import { createTimeArray, dataHandler_alert, mergeMobilityAndAlerts } from './BedStateRow';

const parseAsUTC8 = (str) => new Date(str.replace(' ', 'T') + '+08:00').getTime();

const getItemTime = (item) => {
  const str = item.timestamp ?? item.start_datetime?.replace('T', ' ');
  if (!str) return NaN;
  return parseAsUTC8(str);
};

const BedStateRowThreeDay = (props) => {
  const { t } = useTranslation();
  const ONE_DAY = 86400000;
  const shift = props.shift;
  const state = props.state;
  const store_alerts = props.store_alerts || [];

  const dateStr = String(props.date).slice(0, 10);
  const dateStamp     = new Date(`${dateStr}T08:00:00+08:00`).getTime();  // 當日 08:00
  const dateStamp_2day = dateStamp - 86400000;                             // 一天前 08:00
  const dateStamp_3day = dateStamp - 172800000;                            // 兩天前 08:00

  const [alertRow,    setAlertRow]    = useState([]);  // 當日
  const [alertRowYes, setAlertRowYes] = useState([]);  // 一天前
  const [alertRowTom, setAlertRowTom] = useState([]);  // 兩天前

  const store_macAlertSettings = useSelector((state) => state.store_mac_alert_setting.orign_macAlerts_array)?.[0];

  // ─── 共用：安全取得 ref alert（供 bed_id / resident_id 用）─────────────────
  // 優先從該 row 自己的 alerts 取，若無則往鄰近天找，最後回傳 null
  const getSafeRef = (...alertArrays) => {
    for (const arr of alertArrays) {
      if (arr && arr.length > 0) return arr[0];
    }
    return null;
  };

  // ─── 共用：附加班別/當日終點補丁 ──────────────────────────────────────────
  const appendShiftAndCurrentTimePatch = (row, refAlert, shift, MS, NS, GS, rowDateStamp) => {
    if (!refAlert) return row;  // ← 核心防護：沒有 ref 就不補

    if (shift === 'MS' || shift === 'NS') {
      row.push({
        bedState: 0, bedStateAlertLevel: 0,
        bed_id: refAlert.bed_id, resident_id: refAlert.resident_id,
        index: row.length + 1,
        secs: shift === 'MS' ? MS : NS,
        startTag: 1,
        timestamp: new LogTime().getFullTime_withoutMillsec(),
      });
      row.push({
        bedState: 0, bedStateAlertLevel: 0,
        bed_id: refAlert.bed_id, resident_id: refAlert.resident_id,
        index: row.length + 1,
        secs: shift === 'MS' ? (86400 - NS) : (86400 - GS),
        timestamp: new LogTime().getFullTime_withoutMillsec(),
      });
    } else if (shift === 'GS') {
      row.push({
        bedState: 0, bedStateAlertLevel: 0,
        bed_id: refAlert.bed_id, resident_id: refAlert.resident_id,
        index: row.length + 1,
        secs: GS,
        startTag: 2,
        rightShift: MS,
        timestamp: new LogTime().getFullTime_withoutMillsec(),
      });
    }

    // 若 rowDateStamp 對應今天，補「到現在為止」的終點
    const nowMs = Date.now();
    if (nowMs >= rowDateStamp && nowMs < rowDateStamp + ONE_DAY) {
      const currentTime = Date.now();
      row.push({
        bedState: 0, bedStateAlertLevel: 0,
        bed_id: refAlert.bed_id, resident_id: refAlert.resident_id,
        index: row.length + 1,
        secs: (rowDateStamp + ONE_DAY - currentTime) / 1000,
        left: ((currentTime - rowDateStamp) / ONE_DAY) * 100,
        timestamp: new LogTime().getFullTime_withoutMillsec(),
      });
    }

    return row;
  };

  // ══════════════════════════════════════════════════════════════
  //  useEffect 1：當日（dateStamp）
  //  資料來源：alertsYesterday + store_alerts(current) + alertsNextDay + alertsNextNextDay
  // ══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!store_macAlertSettings || dateStamp === null) return;

    // Fix 1: 增加備援來源
    const refAlert = getSafeRef(
      store_alerts,
      props.alertsYesterday,
      props.alertsTommorrow,
      props.prevAlertsForThreeDay
    );
    if (!refAlert) return;

    const MS = store_macAlertSettings.day_start_at;
    const NS = store_macAlertSettings.mid_start_at;
    const GS = store_macAlertSettings.night_start_at;

    const earlyMorningAlerts   = filterEarlyMorningAlerts(props.alertsNextDay, dateStamp + ONE_DAY);
    const earlyMorningMobility = filterEarlyMorningAlerts(props.mobilityDataNextDay, dateStamp + ONE_DAY);

    // Fix 2: combinedAlerts 補入 prevAlertsForThreeDay 和 alertsTommorrow
    const combinedAlerts = [
      ...( props.prevAlertsForThreeDay || []),  // ← 新增
      ...( props.alertsTommorrow       || []),  // ← 新增
      ...( props.alertsYesterday       || []),
      ...store_alerts,
      ...( props.alertsNextDay         || []),
      ...( props.alertsNextNextDay     || []),
    ];

    const combinedMobility = [
      ...( props.mobilityData   || []),
      ...earlyMorningMobility,
    ];

    const result = mergeMobilityAndAlerts(
      combinedMobility, combinedAlerts, dateStamp,
      props.isLastActiveDayCurrent || false
    );

    let row = [...result];
    row = appendShiftAndCurrentTimePatch(row, refAlert, shift, MS, NS, GS, dateStamp);
    setAlertRow(row);

    return () => setAlertRow([]);
  }, [
    store_alerts,
    props.alertsTommorrow,        // ← 新增 dependency
    props.prevAlertsForThreeDay,  // ← 新增 dependency
    props.alertsNextDay,
    props.alertsNextNextDay,
    props.mobilityDataNextDay,
    props.mobilityData,
    props.alertsYesterday,
    dateStamp, shift, store_macAlertSettings, props.date,
  ]);

  // ══════════════════════════════════════════════════════════════
  //  useEffect 2：一天前（dateStamp_2day）
  //  資料來源：alertsTommorrow(兩天前) + alertsYesterday(一天前) + store_alerts(當日)
  // ══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!store_macAlertSettings || dateStamp_2day === null) return;

    // ← 一天前 row 的 refAlert：優先 yesterday，否則 tommorrow / current
    const refAlert = getSafeRef(props.alertsYesterday, props.alertsTommorrow, store_alerts, props.prevAlertsForThreeDay, props.alertsNextDay);
    if (!refAlert) return;

    const MS = store_macAlertSettings.day_start_at;
    const NS = store_macAlertSettings.mid_start_at;
    const GS = store_macAlertSettings.night_start_at;

    const earlyMorningMobility = filterEarlyMorningAlerts(props.mobilityData, dateStamp);

    const combinedAlerts = [
      ...( props.prevAlertsForThreeDay || []),
      ...( props.alertsTommorrow || []),
      ...( props.alertsYesterday || []),
      ...store_alerts,
    ];
    const combinedMobility = [
      ...( props.mobilityDataYesterday || []),
      ...earlyMorningMobility,
    ];
    const result = mergeMobilityAndAlerts(combinedMobility, combinedAlerts, dateStamp_2day, props.isLastActiveDayYesterday || false);
    let row = [...result];
    row = appendShiftAndCurrentTimePatch(row, refAlert, shift, MS, NS, GS, dateStamp_2day);
    setAlertRowYes(row);

    return () => setAlertRowYes([]);
  }, [
    props.alertsYesterday,
    props.alertsTommorrow,
    props.mobilityDataYesterday,
    props.mobilityDataTommorrow,
    props.alertsNextDay,
    props.mobilityDataNextDay,
    props.mobilityData,
    store_alerts,
    dateStamp_2day,
    dateStamp,
    shift,
    store_macAlertSettings,
  ]);

  // ══════════════════════════════════════════════════════════════
  //  useEffect 3：兩天前（dateStamp_3day）
  //  資料來源：alertsTommorrow(兩天前) + alertsYesterday(一天前) + store_alerts(當日)
  // ══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!store_macAlertSettings || dateStamp_3day === null) return;

    // ← 兩天前 row 的 refAlert：優先 tommorrow，否則 yesterday / current
    const refAlert = getSafeRef(props.alertsTommorrow, props.alertsYesterday, store_alerts, props.prevAlertsForThreeDay, props.alertsNextDay);
    if (!refAlert) return;

    const MS = store_macAlertSettings.day_start_at;
    const NS = store_macAlertSettings.mid_start_at;
    const GS = store_macAlertSettings.night_start_at;

    const earlyMorningMobility = filterEarlyMorningAlerts(props.mobilityDataYesterday, dateStamp_2day);

    const combinedAlerts = [
      ...( props.prevAlertsForThreeDay || []),
      ...( props.alertsTommorrow || []),
      ...( props.alertsYesterday || []),
      ...store_alerts,
    ];
    const combinedMobility = [
      ...( props.mobilityDataTommorrow || []),
      ...earlyMorningMobility,
    ];
    const result = mergeMobilityAndAlerts(combinedMobility, combinedAlerts, dateStamp_3day, props.isLastActiveDayTwoDaysAgo || false);
    let row = [...result];
    row = appendShiftAndCurrentTimePatch(row, refAlert, shift, MS, NS, GS, dateStamp_3day);
    setAlertRowTom(row);

    return () => setAlertRowTom([]);
  }, [
    props.alertsTommorrow,
    props.mobilityDataTommorrow,
    props.alertsYesterday,
    props.mobilityDataYesterday,
    props.alertsNextDay,
    props.mobilityDataNextDay,
    props.mobilityData,
    store_alerts,
    dateStamp_3day,
    dateStamp_2day,
    dateStamp,
    shift,
    store_macAlertSettings,
  ]);

  return (
    <div className="w-100 h-80 blue_bgC mar-5 t fx-d-c fx-cspar">
      <div className="w-100 fx-d-r br-10 h-30 fx-fsc pos-r z-9">
        <div className="w-10 fx-ccc">
          <div className="h-60 fx-ccc w-100">{t('HISTORY_RECORD.TODAY')}</div>
        </div>
        <div className="bgC-border-bold w-100 font-s pos-r z-max h-40">
          {alertRow.length !== 0
            ? alertRow.map((res, index) => {
                const _width = `${(res.secs / 86400) * 100}%`;
                return <StatePeriod state={state} rightShift={res.rightShift} startTag={res.startTag} key={index} width={_width} res={res} index={index} />;
              })
            : null}
        </div>
      </div>

      <div className="w-100 fx-d-r br-10 h-30 fx-fsc pos-r z-5">
        <div className="w-10 fx-ccc">
          <div className="h-60 fx-ccc w-100 ">{t('HISTORY_RECORD.ONE_DAY_AGO')}</div>
        </div>
        <div className="bgC-border-bold w-100 font-s pos-r z-max h-40">
          {alertRowYes.length !== 0
            ? alertRowYes.map((res, index) => {
                const _width = `${(res.secs / 86400) * 100}%`;
                return <StatePeriod state={state} rightShift={res.rightShift} startTag={res.startTag} key={index} width={_width} res={res} index={index} />;
              })
            : null}
        </div>
      </div>

      <div className="w-100 fx-d-r br-10 h-30 fx-fsc pos-r z-1">
        <div className="w-10 fx-ccc">
          <div className="h-60 fx-ccc w-100 ">{t('HISTORY_RECORD.TWO_DAYS_AGO')}</div>
        </div>
        <div className="bgC-border-bold w-100 font-s pos-r z-max h-40">
          {alertRowTom.length !== 0
            ? alertRowTom.map((res, index) => {
                const _width = `${(res.secs / 86400) * 100}%`;
                return <StatePeriod state={state} rightShift={res.rightShift} startTag={res.startTag} key={index} width={_width} res={res} index={index} />;
              })
            : null}
        </div>
      </div>

      <HourInterval width={'w-100'} date={props.date} />
      <LegendRow />
    </div>
  );
};

export default BedStateRowThreeDay;

// ------ 時間軸圖表標示 -------------------------
const HourInterval = (props) => {
  const { t } = useTranslation();
  const timeArray = createTimeArray();

  return (

    <div className="w-100 fx-d-r br-10 fx-fsc h-20">
      <div className="w-10 fx-ccc">
        <div className="h-60 fx-ccc w-100">{t('ADMIN.CONNECTION_HIS_PAGE.TIME_LINE')}</div>
      </div>
      <div className={`${props.width} fx-d-r font-s fx-cspbt h-3`}>
        {timeArray.map((time, index) => (
          <div
            className={`
              ${time.minute % 20 === 0 ? 'right_border' : ''}
              ${time.minute === 0 ? 'h-hours right_border_2' : 'h-minutes'}
            `}
            key={index}>
            <div className="pos-a left-hour">
              {time.minute === 0 ? (time.hour < 10 ? `0${time.hour}` : time.hour) : null}
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

// ------ 每個狀態區段 -------------------------
const StatePeriod = (props) => {
  const res = props.res;
  const { t } = useTranslation();
  const [mouseX, setMouseX]   = useState(0);
  const [hoverBtm, setHoverBtm] = useState(false);

  function toggleHover() { setHoverBtm(!hoverBtm); }

  const width = window.innerWidth;
  function getMousePosition(e) {
    toggleHover();
    setMouseX(Math.floor(e.clientX / width));
  }

  const dh             = dataHandler_alert(res.bedStateAlertLevel, res.bedState);
  const level          = dh.level;
  const msg            = dh.msg;
  const resolvedColor  = transColor(res.bedState, res.bedStateAlertLevel, props.state);

  const _style = {
    width:  props.width,
    left:   `${res.left}%`,
    zIndex: `${res.index}`,
  };

  return (
    <div style={_style} onMouseEnter={getMousePosition} onMouseLeave={getMousePosition}
         className={`${resolvedColor} h-100 pos-a fx-d-r`}>
      {hoverBtm && resolvedColor !== 'bgC-blueBACK' ? (
        <div className="bgC-white pos-a hover_card_his" style={{ left: `${mouseX}%`, top: 10 }}>
          <div className={`w-100 h-15 topping ${resolvedColor}`}></div>
          <div className="fx-d-r fx-cspar h-75">
            <div>
              <div>{t(res.timestamp)}</div>
              {/* <div>
                {level === 'normal' ? t('GLOBAL.BED_STATE.LEVEL1') : level === 'warning' ? t('GLOBAL.BED_STATE.LEVEL2') : t('GLOBAL.BED_STATE.LEVEL3')}：{t(msg)}
              </div> */}
              <div>
                {t(msg)}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {level === 'emergency' ? <div className="bedStateRowTag"></div> : null}
    </div>
  );
};

// ------ 圖例 -------------------------
const LegendRow = () => {
  const { t } = useTranslation();
  const row1 = [[2, 217], [1, 206], [1, 203]];
  const row3 = [[0, 4],   [0, 12],  [0, 1]];

  return (
    <div className="w-100 h-70 fx-d-r fx-cspar mar-15 t">
      <div className="h-100 w-40 fx-d-c legend">
        <div className="w-40 h-20 fx-ccc mar-5 l">{t('GLOBAL.NOTIFICATION')}</div>
        <div className="gridLegend_row h-70 fx-d-c">
          {row1.map((res) => (
            <div className="h-80 w-100 fx-d-r fx-ccc" key={String(res[1]) + String(res[0])}>
              <div className={`w-15 h-30 bgC-border ${transColor(res[1], res[0])}`}></div>
              <div className="fx-d-r fx-fsc w-60">
                <div className="w-100" style={{ paddingLeft: '50px' }}>
                  {t(dataHandler_alert(res[0], res[1]).msg)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="h-100 w-40 fx-d-c legend">
        <div className="w-40 h-20 fx-ccc mar-5 l">{t('GLOBAL.BED_state')}</div>
        <div className="gridLegend_row h-70 fx-d-c">
          {row3.map((res) => (
            <div className="h-80 w-100 fx-d-r fx-ccc" key={String(res[1]) + String(res[0])}>
              <div className={`w-15 h-30 bgC-border ${transColor(res[1], res[0])}`}></div>
              <div className="fx-d-r fx-fsc w-60">
                <div className="w-100" style={{ paddingLeft: '50px' }}>
                  {t(dataHandler_alert(res[0], res[1]).msg)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ------ 顏色工具 -------------------------
const LYING_STATES = new Set([4, 7, 8, 13, 15, 103, 501, 502, 503, 504, 505, 511, 512]);
const HIDE_STATES  = new Set([208, 211, 212]);
const toNum = (v) => Number(v);
const shouldHideState = (state) => HIDE_STATES.has(toNum(state));
const shouldBeLying   = (state) => LYING_STATES.has(toNum(state));

function transColor(state, level, selectedLevel) {
  const s = Number(state);
  if (shouldBeLying(s)) return 'bgC-lying';
  if (level === 2) {
    if (s === 14 || s === 12) return 'bgC-sit';
    if (s === 1 || s === 218) return 'bgC-NIB';
    if (s === 217) return 'bgC-longLying';
    return 'bgC-light-pink';
  } else if (level === 1) {
    if (s === 218) return 'bgC-NIB';
    if (s === 206) return 'bgC-dark-pink';
    if (s === 203) return 'bgC-zigzag-pink';
    return 'bgC-sit';
  } else {
    if (s === 1)         return 'bgC-NIB';
    if (s === 0)         return 'bgC-blueBACK';
    if (s === 12 || s === 14) return 'bgC-sit';
    if (s === 203)       return 'bgC-blue_2';
    if (s === 206)       return 'bgC-dark-pink';
    if (s === 208)       return 'bgC-blueBACK';
    if (s === 217)       return 'bgC-blue_2';
    if (s === 218)       return 'bgC-NIB';
    return 'bgC-blue_2';
  }
}

// ------ 過濾凌晨資料（00:00~08:00 UTC+8）-------------------------
function filterEarlyMorningAlerts(alerts, utcMidnight) {
  if (!alerts || alerts.length === 0) return [];
  const windowStart = utcMidnight - 28800000;
  const windowEnd   = utcMidnight;
  return alerts.filter((alert) => {
    const ts = getItemTime(alert);
    return ts >= windowStart && ts < windowEnd;
  });
}